import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/ui/PageHeader';
import KpiCard from '@/components/ui/KpiCard';
import { Panel, PanelHeader } from '@/components/ui/Panel';
import StatusBadge from '@/components/ui/StatusBadge';
import { partners } from '@/mocks/partners';
import { products } from '@/mocks/products';
import { ordersApi, poTone, type Arrival, type OrderRow } from '@/lib/orders';
import { aed, dayMonth, num, shortDate } from '@/lib/format';

export default function Dashboard() {
  const { t } = useTranslation();
  const [arrivals, setArrivals] = useState<Arrival[]>([]);
  const [recentOrders, setRecentOrders] = useState<OrderRow[]>([]);
  useEffect(() => {
    ordersApi.arrivals(3).then(setArrivals).catch(() => setArrivals([]));
    ordersApi.list().then((d) => setRecentOrders(d.orders.slice(0, 4))).catch(() => setRecentOrders([]));
  }, []);
  const stalePartners = partners.filter((p) => p.staleDays > 7);
  const activePartners = partners.filter((p) => p.status === 'Active');
  const totalSkus = partners.reduce((sum, p) => sum + p.skus, 0);
  const totalUnits = partners.reduce((sum, p) => sum + p.units, 0);
  const stockValue = products.reduce((sum, p) => sum + p.stock * p.cost, 0);
  const canPublish = partners.filter((p) => p.contractStatus === 'Signed').length;

  const statusTone = (status: string) =>
    status === 'Active' ? 'primary' : status === 'Paused' ? 'danger' : 'accent';

  return (
    <div>
      <PageHeader
        eyebrow={t('dashboard.eyebrow')}
        title={t('dashboard.title')}
        description={t('dashboard.description')}
        actions={
          <>
            <Link
              to="/import"
              className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-upload-cloud-2-line text-[14px]" />
              {t('dashboard.importBtn')}
            </Link>
            <Link
              to="/partners"
              className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-store-2-line text-[14px]" />
              {t('dashboard.manageBtn')}
            </Link>
          </>
        }
      />

      {stalePartners.length > 0 && (
        <div className="mb-5 flex flex-col gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2.5">
            <i className="ri-time-line mt-0.5 text-[15px] text-accent-700" />
            <p className="text-[12.5px] leading-5 text-accent-900">
              <span className="font-semibold">{t('dashboard.staleCount', { count: stalePartners.length })}</span>{' '}
              {t('dashboard.staleBody', { names: stalePartners.map((p) => p.shortName).join(' · ') })}
            </p>
          </div>
          <Link
            to="/partners"
            className="inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-md border border-accent-300 bg-background-50 px-2.5 py-1 text-[11.5px] font-semibold text-accent-900 transition-colors hover:bg-accent-100"
          >
            {t('dashboard.review')} <i className="ri-arrow-right-line text-[11px]" />
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <KpiCard
          label={t('dashboard.kpiActive')}
          value={String(activePartners.length)}
          icon="ri-store-3-line"
          trend="up"
          trendLabel={t('dashboard.addedOct')}
          hint={t('dashboard.kpiActiveHint', { count: partners.length })}
        />
        <KpiCard
          label={t('dashboard.kpiSkus')}
          value={num(totalSkus)}
          icon="ri-price-tag-3-line"
          trend="up"
          trendLabel={t('dashboard.weekChange')}
        />
        <KpiCard
          label={t('dashboard.kpiUnits')}
          value={num(totalUnits)}
          icon="ri-archive-2-line"
          trend="down"
          trendLabel={t('dashboard.resaleTrend')}
          hint={t('dashboard.liveSnapshot')}
        />
        <KpiCard
          label={t('dashboard.kpiValue')}
          value={`AED ${(stockValue / 1000000).toFixed(2)}M`}
          icon="ri-wallet-3-line"
          trend="up"
          trendLabel={t('dashboard.shareTrend')}
          accent
        />
        <KpiCard
          label={t('dashboard.kpiStale')}
          value={String(stalePartners.length)}
          icon="ri-alert-line"
          trend="flat"
          trendLabel={t('dashboard.overSeven')}
          hint={t('dashboard.kpiStaleHint')}
        />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel padded={false} className="xl:col-span-2">
          <PanelHeader
            title={t('dashboard.freshnessTitle')}
            subtitle={t('dashboard.freshnessSub')}
            action={
              <Link to="/partners" className="text-[11.5px] font-semibold text-primary-700 hover:text-primary-800">
                {t('dashboard.viewAll')}
              </Link>
            }
          />
          <div className="overflow-x-auto thin-scroll">
            <table className="w-full min-w-[720px] border-collapse text-[12.5px]">
              <thead>
                <tr className="border-y border-background-200 bg-background-100/70 text-left text-[10.5px] uppercase tracking-[0.08em] text-foreground-500">
                  <th className="px-4 py-2 font-medium">{t('dashboard.colPartner')}</th>
                  <th className="px-3 py-2 text-right font-medium">{t('dashboard.colSkus')}</th>
                  <th className="px-3 py-2 text-right font-medium">{t('dashboard.colUnits')}</th>
                  <th className="px-3 py-2 font-medium">{t('dashboard.colDiscount')}</th>
                  <th className="px-3 py-2 font-medium">{t('dashboard.colLast')}</th>
                  <th className="px-4 py-2 font-medium">{t('common.status')}</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((p) => (
                  <tr
                    key={p.id}
                    className="group border-b border-background-200/70 transition-colors last:border-0 hover:bg-background-100/70"
                  >
                    <td className="px-4 py-2.5">
                      <Link to={`/partners/${p.id}`} className="flex items-center gap-2.5">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-secondary-100 text-[10px] font-semibold text-secondary-800">
                          {p.shortName.slice(0, 2).toUpperCase()}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-foreground-900 group-hover:text-primary-700">
                            {p.name}
                          </span>
                          <span className="block text-[11px] text-foreground-400">{p.city}</span>
                        </span>
                      </Link>
                    </td>
                    <td className="tnum px-3 py-2.5 text-right text-foreground-700">{p.skus}</td>
                    <td className="tnum px-3 py-2.5 text-right text-foreground-700">{num(p.units)}</td>
                    <td className="tnum px-3 py-2.5 text-foreground-700">
                      {p.discount > 0 ? `${p.discount}%` : t('status.Special only')}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="tnum text-foreground-700">{shortDate(p.lastUpdate)}</span>
                        {p.staleDays > 7 && (
                          <StatusBadge tone="accent" className="!px-1.5 !text-[10px]">
                            {t('dashboard.staleBadge', { days: p.staleDays })}
                          </StatusBadge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge tone={statusTone(p.status) as 'primary' | 'danger' | 'accent'}>
                        {t(`status.${p.status}`)}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-background-200 px-4 py-2.5">
            <p className="text-[11px] text-foreground-400">
              {t('dashboard.contractGate', { can: canPublish, total: partners.length })}
            </p>
            <Link to="/import" className="text-[11.5px] font-semibold text-primary-700 hover:text-primary-800">
              {t('dashboard.importNew')} <i className="ri-arrow-right-line text-[11px]" />
            </Link>
          </div>
        </Panel>

        <div className="flex flex-col gap-4">
          <Panel padded={false}>
            <PanelHeader
              title={t('dashboard.incomingTitle')}
              subtitle={t('dashboard.incomingSub')}
              action={
                <span className="tnum text-[11px] text-foreground-400">
                  {num(arrivals.reduce((sum, s) => sum + s.qty, 0))} {t('common.units')}
                </span>
              }
            />
            <ul className="divide-y divide-background-200 border-t border-background-200">
              {arrivals.slice(0, 4).map((s) => (
                <li key={`${s.poNumber}-${s.sku}`} className="flex items-center gap-3 px-4 py-2.5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded border border-background-200 bg-background-100">
                    {s.image ? <img src={s.image} alt={s.name ?? ''} className="h-full w-full object-contain" /> : <i className="ri-archive-2-line text-foreground-300" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-medium text-foreground-900">{s.sku ?? s.name}</p>
                    <p className="truncate text-[11px] text-foreground-500">{s.partnerName}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="tnum text-[12px] font-semibold text-foreground-900">+{s.qty}</p>
                    <p className="tnum text-[10.5px] text-foreground-500">{s.eta ? dayMonth(s.eta) : '—'}</p>
                  </div>
                </li>
              ))}
              {arrivals.length === 0 && <li className="px-4 py-3 text-[11.5px] text-foreground-500">{t('ord.noArrivals')}</li>}
            </ul>
            <div className="border-t border-background-200 px-4 py-2.5">
              <Link to="/catalog" className="text-[11.5px] font-semibold text-primary-700 hover:text-primary-800">
                {t('dashboard.openCatalog')} <i className="ri-arrow-right-line text-[11px]" />
              </Link>
            </div>
          </Panel>

          <Panel padded={false} className="flex-1">
            <PanelHeader
              title={t('dashboard.recentPo')}
              action={
                <Link to="/purchase-orders" className="text-[11.5px] font-semibold text-primary-700 hover:text-primary-800">
                  {t('dashboard.allPo')}
                </Link>
              }
            />
            <ul className="divide-y divide-background-200 border-t border-background-200">
              {recentOrders.map((po) => (
                <li key={po.poNumber}>
                  <Link
                    to={`/purchase-orders/${encodeURIComponent(po.poNumber)}`}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 transition-colors hover:bg-background-100/70"
                  >
                    <div className="min-w-0">
                      <p className="tnum text-[12px] font-semibold text-foreground-900">{po.poNumber}</p>
                      <p className="truncate text-[11px] text-foreground-500">{po.partnerName}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="tnum text-[12px] font-medium text-foreground-800">{aed(po.total)}</p>
                      <p className={`text-[10.5px] ${poTone(po.fulfillmentStatus) === 'primary' ? 'text-primary-700' : 'text-foreground-500'}`}>{t(`status.${po.fulfillmentStatus}`, po.fulfillmentStatus)}</p>
                    </div>
                  </Link>
                </li>
              ))}
              {recentOrders.length === 0 && <li className="px-4 py-3 text-[11.5px] text-foreground-500">{t('ord.emptyAll')}</li>}
            </ul>
          </Panel>
        </div>
      </div>
    </div>
  );
}